import { Box, IconButton, Link, ListItemText, Tooltip } from '@mui/material';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { cloneElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { NavItemProps } from '../types';
import { StyledDotIcon, StyledIcon, StyledItem } from './styles';

export default function NavItem({
  item,
  depth,
  open,
  active,
  isExternalLink,
  onToggleOpen,
  moreOptionsMenu,
  ...other
}: NavItemProps) {
  const { translate } = useLocales();

  const { title, hideTitle, path, icon, info, children, disabled, caption } =
    item;

  const subItem = depth !== 1;

  const renderContent = (
    <StyledItem
      depth={depth}
      active={active}
      disabled={disabled}
      caption={!!caption}
      {...other}
    >
      {icon && (
        <Tooltip
          title={hideTitle ? `${translate(title)}` : ''}
          placement="right"
        >
          <StyledIcon>{icon}</StyledIcon>
        </Tooltip>
      )}

      {subItem && (
        <StyledIcon>
          <StyledDotIcon active={active && subItem} />
        </StyledIcon>
      )}

      {!hideTitle && (
        <ListItemText
          primary={`${translate(title)}`}
          secondary={
            caption && (
              <Tooltip title={`${translate(caption)}`} placement="top-start">
                <span>{`${translate(caption)}`}</span>
              </Tooltip>
            )
          }
          primaryTypographyProps={{
            noWrap: true,
            component: 'span',
            variant: active ? 'subtitle2' : 'body2',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            variant: 'caption',
          }}
        />
      )}

      {!hideTitle && info && (
        <Box component="span" sx={{ lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {!hideTitle && moreOptionsMenu && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <PopupState variant="popover" popupId="demoMenu">
            {(popupState) => {
              const { onClick, ...rest } = bindTrigger(popupState);
              return (
                <>
                  <IconButton
                    size="small"
                    {...rest}
                    onClick={(e) => {
                      onClick(e);
                    }}
                  >
                    <Iconify icon="tabler:dots-vertical" />
                  </IconButton>
                  {cloneElement(
                    moreOptionsMenu(popupState) as any,
                    bindMenu(popupState),
                  )}
                </>
              );
            }}
          </PopupState>
        </div>
      )}

      {open !== undefined && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onToggleOpen?.();
          }}
        >
          <Iconify
            icon={
              open
                ? 'eva:arrow-ios-downward-fill'
                : 'eva:arrow-ios-forward-fill'
            }
            sx={{ flexShrink: 0 }}
          />
        </IconButton>
      )}
    </StyledItem>
  );

  // ExternalLink
  if (isExternalLink)
    return (
      <Link href={path} target="_blank" rel="noopener" underline="none">
        {renderContent}
      </Link>
    );

  if (children) {
    return renderContent;
  }

  if (!path) return renderContent;

  return (
    <Link component={RouterLink} to={path} underline="none">
      {renderContent}
    </Link>
  );
}
